import { CarIcon, UserCircleIcon, type Icon } from '@phosphor-icons/react';
import type { TFunction } from 'i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { UndefinedIdentityCellConfig } from '@/components/IdentityAvatarGrid';
import type { EntityKind, Identity, IdentityToken } from '@/types/identity';

const ENTITY_KINDS: readonly EntityKind[] = ['person', 'vehicle'];
const UNDEFINED_PREFIX = 'undefined';
const TOKEN_SEPARATOR = ',';

export function identityToken(identity: Identity): IdentityToken {
    return `${identity.kind}:${identity.id}`;
}

export function undefinedIdentityToken(kind: EntityKind): IdentityToken {
    return `${UNDEFINED_PREFIX}:${kind}`;
}

export function isIdentityToken(value: string): value is IdentityToken {
    const [prefix, remainder] = value.split(':');
    if (!remainder) {
        return false;
    }
    if (prefix === UNDEFINED_PREFIX) {
        return ENTITY_KINDS.some((kind) => kind === remainder);
    }
    return ENTITY_KINDS.some((kind) => kind === prefix);
}

export function parseIdentityTokens(value: string | null): Set<IdentityToken> {
    if (!value) {
        return new Set();
    }
    return new Set(value.split(TOKEN_SEPARATOR).filter(isIdentityToken));
}

export function encodeIdentityTokens(
    tokens: Set<IdentityToken>,
): string | null {
    if (tokens.size === 0) {
        return null;
    }
    return Array.from(tokens).join(TOKEN_SEPARATOR);
}

export function getIdentityFallbackIcon(kind: EntityKind): Icon {
    return kind === 'person' ? UserCircleIcon : CarIcon;
}

export function getIdentityLabel(t: TFunction, identity: Identity): string {
    if (identity.name) {
        return identity.name;
    }
    if (identity.licensePlate) {
        return identity.licensePlate;
    }
    return t('identityFilter.unknown', { ns: I18nNamespace.COMMON });
}

const UNDEFINED_LABEL_KEYS: Record<EntityKind, string> = {
    person: 'identityFilter.undefinedPersons',
    vehicle: 'identityFilter.undefinedVehicles',
};

const UNDEFINED_DESCRIPTION_KEYS: Record<EntityKind, string> = {
    person: 'identityFilter.undefinedPersonsDescription',
    vehicle: 'identityFilter.undefinedVehiclesDescription',
};

interface UndefinedIdentityCellOptions {
    kind: EntityKind;
    count: number;
    t: TFunction;
    // Surfaces that show one bucket per tab let the tab name the kind, so their
    // label stays short. The sidebar shows both at once and has to spell it out.
    labelPerKind: boolean;
}

export function buildUndefinedIdentityCell({
    kind,
    count,
    t,
    labelPerKind,
}: UndefinedIdentityCellOptions): UndefinedIdentityCellConfig {
    return {
        token: undefinedIdentityToken(kind),
        kind,
        label: t(
            labelPerKind
                ? UNDEFINED_LABEL_KEYS[kind]
                : 'identityFilter.undefined',
            { ns: I18nNamespace.COMMON },
        ),
        description: t(UNDEFINED_DESCRIPTION_KEYS[kind], {
            ns: I18nNamespace.COMMON,
        }),
        count,
    };
}
